type Choices = ReadonlyArray<{ text: string }>;

export interface QuestionsAnswers
  extends Readonly<{
    questionText: string;
    openAnswerText: string;
    choices: Choices;
  }> {}

export const weddingInvitationsPageData = {
  seo: {
    title: 'Wedding Invitations | Paper + Free Online Templates | Joy',
    content:
      'Send custom wedding invitations to match your wedding website or choose from 100+ designs. Order prints or send free online invites. Online guest list & RSVP.',
  },
  hero: {
    eyebrow: 'Send Smarter',
    title: 'Wedding Invitations',
    posterUrl: 'invites-gallery/hero.jpg',
    videoUrl:
      'https://player.vimeo.com/video/1007823461?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
  },
  connectedGuestSection: {
    eyebrow: 'The Connected Guest List',
    title: 'Powerfully inviting.',
    body:
      'Your invites are powered by the best guest list on Earth, with intelligent tags, flexible RSVP, custom questions, and more brilliant features that let you plan smarter.',
    exploreGuestList: 'Explore Guest List',
  },
  highQualityPrintsSection: {
    eyebrow: 'High-Quality Prints',
    title: 'To Have <i>and</i> To Hold',
    body:
      'Printing and mailing? Our wedding invitation templates with matching prints from our partners let you add premium touches. Whether you want foil wedding invitations or letterpress, envelope liners or a matte finish monogram, you can create modern wedding invitations that make your big day perfectly yours.',
    exploreDesigns: 'Explore Designs',
  },
  highQualityPrintsSectionV2: {
    eyebrow: 'High-Quality Prints',
    title: 'To Have <i>and</i> To Hold',
    body:
      'Our wedding invitation templates with matching digital cards let you add premium touches. Whether you want a traditional invitation suite or a modern all-in-one, envelope liners or gold foil, you can create unique wedding invitations that make your big day perfectly yours.',
    ctaText: 'Explore Designs',
    leftImageData: {
      desktopImageUrl: 'invites-gallery/bohemian-eucalyptus.png',
      mobileImageUrl: 'invites-gallery/bohemian-eucalyptus-mobile.png',
    },
    rightImageData: {
      desktopImageUrl: 'invites-gallery/ivory-garden.png',
      mobileImageUrl: 'invites-gallery/ivory-garden-mobile.png',
    },
  },
  invitationsReimaginedSection: {
    eyebrow: 'How It Works',
    title: 'Invitations, reimagined.',
    body:
      'From custom wedding invitations that match your free website, to a guest list ready for all your details, this is the smarter way to plan your big day.',
    getStarted: 'Get Started',
    createInvite: {
      eyebrow: '1',
      title: 'Find the perfect design for your wedding style.',
      body:
        'Start with an invite that matches your style <i>and</i> your website. Select fonts, colors, and art to make your own design. Whether you’re looking for something classic with calligraphy, botanical with eucalyptus, or going wild with wildflowers, create your perfect wedding invitation design.',
      imageUrl: 'invites-gallery/designs-v2.png',
    },
    guestList: {
      eyebrow: '2',
      title: 'Power it all with the Connected Guest List.',
      body:
        'Every invitation is connected right into your guest list. That means you can use different wedding invitation wording for specific guests, or send a private invite just for those invited to your engagement party. From plus ones and households to smart tags for your tables and meal choices, we’ll track every detail.',
      imageUrl: 'invites-gallery/gl-v2.png',
    },
    finallyRSVP: {
      eyebrow: '3',
      title: 'Finally, RSVP that is ready for all the action.',
      body:
        'Multiple events? No problem. Need to remind your #vanlife cousin to RSVP? Done. Ask unlimited, customizable RSVP questions to get all the details you want to know, from meal choice to song requests, right in your guest list. Whether you’re more DIY or are working with a planner, this is your all-in-one wedding planning platform.',
      imageUrl: 'invites-gallery/tracking-v2.png',
    },
  },
  howItWorksSection: {
    eyebrow: 'How It Works',
    title: 'Invitations, reimagined.',
    body:
      'From custom wedding invitations that match your free website, to a guest list ready for all your details, this is the smarter way to plan your big day.',
    getStarted: 'Get Started',
    section1: {
      eyebrow: '1',
      title: 'Find the perfect design for your wedding style, or even upload your own.',
      body:
        'Start with an invite that matches your style and your website. Select fonts, colors, and art to make your own design. Whether you’re looking for something classic with calligraphy, botanical with eucalyptus, or going wild with wildflowers, create your perfect wedding invitation design.',
      imageUrl: 'invites-gallery/customize.jpg',
      videoUrl:
        'https://player.vimeo.com/video/1007056496?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
    },
    section2: {
      eyebrow: '2',
      title: 'Collect your guests’ contact info with one magic link.',
      body:
        'Craft your magic link, share it with your guests, and that’s it! They fill out a form that automatically updates their info on your guest list. Addressing envelopes for your invitations has never been easier.',
      imageUrl: 'invites-gallery/contact-collector.jpg',
      videoUrl:
        'https://player.vimeo.com/video/1009953391?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
    },
    section3: {
      eyebrow: '3',
      title: 'Share your love story with a free wedding website.',
      body:
        'Remind family how you met, display your favorite engagement photos (or video), share your registry, and even help guests book accommodations. The best part? Your website’s flexible features can grow and change as you solidify your plans.',
      imageUrl: 'invites-gallery/match-website.jpg',
      videoUrl: 'https://player.vimeo.com/video/1021054212?badge=0&autopause=0&player_id=0&app_id=58479',
    },
  },
  invitationSmartRSVPSection: {
    eyebrow: 'Smart RSVP',
    title: 'The best kind of know-it-all.',
    body:
      'Create custom RSVP questions to ask your guests about their meal selections, travel plans, and anything else. Ask private questions only for specific groups of guests, and even follow-up questions based on their answers.',
    exploreRSVP: 'Explore RSVP',
  },
  invitationSmartRSVPSectionV2: {
    eyebrow: 'Smart RSVP',
    title: 'Finally, RSVP that is ready for all the action.',
    body:
      'Multiple events? No problem. Need to remind your #vanlife cousin to RSVP? Done. Ask unlimited, customizable RSVP questions to get all the details you want to know, from meal choice to song requests, right in your guest list. Sharing is easy with back-of-card QR codes.',
    exploreRSVP: 'Explore RSVP',
    desktop: {
      posterUrl: 'invites-gallery/smart-qr-v2.jpg',
      videoUrl:
        'https://player.vimeo.com/video/1019975344?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
    },
    mobile: {
      posterUrl: 'invites-gallery/smart-qr-mobile.jpg',
      videoUrl:
        'https://player.vimeo.com/video/1019979698?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479',
    },
  },
  ecoSectionData: {
    eyebrow: 'Environmentally-Friendly Invitations',
    title: 'Share your love with the planet.',
    content:
      'With a tree planted in your honor for every print order, your invitations are planet positive. And with online invitations and your connected guest list, you’re saving more than time and money.',
    contentMobile:
      'With a tree planted in your honor for every print order, your invitations are planet positive. Add in online invitations and your connected guest lift, you’re saving more than time and money.',
    altText: 'Environmental image',
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Take a look at our most commonly asked questions and answers. For additional support chat with us.',
  },
} as const;
